import {
  FieldPosition,
  GamePhase,
  GameStateSnapshot,
  LiveGameEvent,
  LiveMatchState,
  LivePlayer,
  SetResult,
  Strategy,
  TeamSide,
} from './liveMatchTypes.ts';

// ─── Action types ────────────────────────────────────────────────────────────

export type LiveMatchAction =
  | { type: 'INIT'; payload: Omit<LiveMatchState, 'currentPhase' | 'lineupSetupSide' | 'currentSet' | 'currentSetScore' | 'setsWon' | 'completedSets' | 'homeLineup' | 'awayLineup' | 'homeBench' | 'awayBench' | 'homeTimeouts' | 'awayTimeouts' | 'servingTeamNow' | 'eventLog' | 'matchStartedAt' | 'setStartedAt' | 'homeLiberoId' | 'awayLiberoId'> }
  | { type: 'RESTORE'; payload: LiveMatchState }
  | { type: 'SET_SESSION_ID'; payload: number }
  | { type: 'SET_CALLUP'; side: TeamSide; players: LivePlayer[] }
  | { type: 'SET_SHIRT_COLOR'; side: TeamSide; color: string }
  // Navigazione wizard
  | { type: 'GO_TO_STRATEGY' }
  | { type: 'GO_TO_CALLUP' }
  | { type: 'GO_TO_SERVICE' }
  | { type: 'GO_TO_HOME_LINEUP' }
  // Selezioni
  | { type: 'SET_STRATEGY'; payload: Strategy }
  | { type: 'SET_SERVING_TEAM'; payload: TeamSide }
  | { type: 'SET_LINEUP'; side: TeamSide; lineup: FieldPosition[]; bench: number[] }
  | { type: 'SET_LIBERO_ID'; side: TeamSide; playerId: number | null }
  | { type: 'NEXT_LINEUP_SIDE' }
  | { type: 'START_MATCH' }
  | { type: 'ADD_POINT'; scoringTeam: TeamSide }
  | { type: 'UNDO_LAST_EVENT' }
  | { type: 'TIMEOUT'; team: TeamSide }
  | { type: 'SUBSTITUTION'; team: TeamSide; outPlayerId: number; inPlayerId: number }
  | { type: 'UPDATE_SHIRT_NUMBER'; side: TeamSide; playerId: number; shirtNumber: number }
  | { type: 'START_NEW_SET' }
  | { type: 'COMPLETE_MATCH' };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TIMEOUTS_PER_SET = 2;

function setsToWin(strategy: Strategy): number {
  return strategy === 'BEST_OF_3' ? 2 : 3;
}

function pointsToWin(setNumber: number, strategy: Strategy): number {
  const totalSets = strategy === 'BEST_OF_3' ? 3 : 5;
  return setNumber === totalSets ? 15 : 25;
}

export function isSetOver(
  homePoints: number,
  awayPoints: number,
  setNumber: number,
  strategy: Strategy,
): boolean {
  const target = pointsToWin(setNumber, strategy);
  const maxPoints = Math.max(homePoints, awayPoints);
  const minPoints = Math.min(homePoints, awayPoints);
  return maxPoints >= target && maxPoints - minPoints >= 2;
}

export function isMatchOver(setsWon: { home: number; away: number }, strategy: Strategy): boolean {
  const need = setsToWin(strategy);
  return setsWon.home >= need || setsWon.away >= need;
}

/** Ruota la lineup di 1 posizione in senso orario (posizione 1→6→5→4→3→2→1). */
function rotateLineup(lineup: FieldPosition[]): FieldPosition[] {
  // pos 1 va in pos 6, 6→5, 5→4, 4→3, 3→2, 2→1
  const rotation: Record<number, number> = { 1: 6, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5 };
  return lineup.map((fp) => ({ ...fp, position: rotation[fp.position] }));
}

function takeSnapshot(s: LiveMatchState): GameStateSnapshot {
  return {
    homePoints: s.currentSetScore.homePoints,
    awayPoints: s.currentSetScore.awayPoints,
    servingTeamNow: s.servingTeamNow,
    homeLineup: s.homeLineup.map((p) => ({ ...p })),
    awayLineup: s.awayLineup.map((p) => ({ ...p })),
    homeBench: [...s.homeBench],
    awayBench: [...s.awayBench],
    homeTimeouts: s.homeTimeouts,
    awayTimeouts: s.awayTimeouts,
    currentSet: s.currentSet,
    setsWon: { ...s.setsWon },
    completedSets: s.completedSets.map((cs) => ({ ...cs })),
    currentPhase: s.currentPhase,
  };
}

function restoreSnapshot(s: LiveMatchState, snap: GameStateSnapshot): LiveMatchState {
  return {
    ...s,
    currentSetScore: { homePoints: snap.homePoints, awayPoints: snap.awayPoints },
    servingTeamNow: snap.servingTeamNow,
    homeLineup: snap.homeLineup,
    awayLineup: snap.awayLineup,
    homeBench: snap.homeBench,
    awayBench: snap.awayBench,
    homeTimeouts: snap.homeTimeouts,
    awayTimeouts: snap.awayTimeouts,
    currentSet: snap.currentSet,
    setsWon: snap.setsWon,
    completedSets: snap.completedSets,
    currentPhase: snap.currentPhase,
  };
}

// ─── Initial state factory ───────────────────────────────────────────────────

export function createInitialState(
  partial: Omit<LiveMatchState, 'currentPhase' | 'lineupSetupSide' | 'currentSet' | 'currentSetScore' | 'setsWon' | 'completedSets' | 'homeLineup' | 'awayLineup' | 'homeBench' | 'awayBench' | 'homeTimeouts' | 'awayTimeouts' | 'servingTeamNow' | 'eventLog' | 'matchStartedAt' | 'setStartedAt' | 'homeLiberoId' | 'awayLiberoId'>,
): LiveMatchState {
  return {
    ...partial,
    homeLiberoId: null,
    awayLiberoId: null,
    currentPhase: 'CALLUP_SELECTION',
    lineupSetupSide: 'HOME',
    currentSet: 1,
    currentSetScore: { homePoints: 0, awayPoints: 0 },
    setsWon: { home: 0, away: 0 },
    completedSets: [],
    homeLineup: [],
    awayLineup: [],
    homeBench: [],
    awayBench: [],
    homeTimeouts: 0,
    awayTimeouts: 0,
    servingTeamNow: null,
    eventLog: [],
    matchStartedAt: null,
    setStartedAt: null,
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function liveMatchReducer(state: LiveMatchState, action: LiveMatchAction): LiveMatchState {
  switch (action.type) {

    case 'INIT':
      return createInitialState(action.payload);

    case 'RESTORE':
      return action.payload;

    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload };

    case 'SET_CALLUP': {
      if (action.side === 'HOME') return { ...state, homeCallup: action.players };
      return { ...state, awayCallup: action.players };
    }

    case 'SET_SHIRT_COLOR': {
      if (action.side === 'HOME') return { ...state, homeShirtColor: action.color };
      return { ...state, awayShirtColor: action.color };
    }

    // Navigazione wizard (back/forward senza modificare dati)
    case 'GO_TO_CALLUP':
      return { ...state, currentPhase: 'CALLUP_SELECTION' };
    case 'GO_TO_STRATEGY':
      return { ...state, currentPhase: 'STRATEGY_SELECTION' };
    case 'GO_TO_SERVICE':
      return { ...state, currentPhase: 'SERVICE_SELECTION' };
    case 'GO_TO_HOME_LINEUP':
      return { ...state, currentPhase: 'LINEUP_SETUP', lineupSetupSide: 'HOME' };

    case 'SET_STRATEGY':
      return { ...state, strategy: action.payload, currentPhase: 'SERVICE_SELECTION' };

    case 'SET_SERVING_TEAM':
      return { ...state, servingTeam: action.payload, currentPhase: 'LINEUP_SETUP', lineupSetupSide: 'HOME' };

    case 'SET_LINEUP': {
      if (action.side === 'HOME') {
        return { ...state, homeLineup: action.lineup, homeBench: action.bench };
      }
      return { ...state, awayLineup: action.lineup, awayBench: action.bench };
    }

    case 'SET_LIBERO_ID': {
      if (action.side === 'HOME') return { ...state, homeLiberoId: action.playerId };
      return { ...state, awayLiberoId: action.playerId };
    }

    case 'NEXT_LINEUP_SIDE': {
      if (state.lineupSetupSide === 'HOME') {
        return { ...state, lineupSetupSide: 'AWAY' };
      }
      // Entrambe le lineup sono pronte → vai in IN_GAME
      return { ...state, currentPhase: 'IN_GAME' };
    }

    case 'START_MATCH': {
      const now = Date.now();
      return {
        ...state,
        currentPhase: 'IN_GAME',
        // Usa servingTeamNow se già impostato da START_NEW_SET (set 2+), altrimenti usa il server del match (set 1)
        servingTeamNow: state.servingTeamNow ?? state.servingTeam,
        matchStartedAt: state.matchStartedAt ?? now,
        setStartedAt: now,
      };
    }

    case 'ADD_POINT': {
      if (!state.strategy) return state;

      const snapshot = takeSnapshot(state);
      const isHome = action.scoringTeam === 'HOME';
      const newHome = isHome
        ? state.currentSetScore.homePoints + 1
        : state.currentSetScore.homePoints;
      const newAway = !isHome
        ? state.currentSetScore.awayPoints + 1
        : state.currentSetScore.awayPoints;

      // Rotazione: se la squadra che segna era quella in ricezione (non stava battendo), ora guadagna il servizio
      let newHomeLineup = state.homeLineup;
      let newAwayLineup = state.awayLineup;
      let newServingTeamNow = state.servingTeamNow;

      const prevServing = state.servingTeamNow;
      if (action.scoringTeam !== prevServing) {
        // Side-out: la squadra che riceveva segna → ruota e prende il servizio
        if (action.scoringTeam === 'HOME') {
          newHomeLineup = rotateLineup(state.homeLineup);
          newServingTeamNow = 'HOME';
        } else {
          newAwayLineup = rotateLineup(state.awayLineup);
          newServingTeamNow = 'AWAY';
        }
      }

      const event: LiveGameEvent = {
        type: isHome ? 'POINT_HOME' : 'POINT_AWAY',
        timestamp: Date.now(),
        setNumber: state.currentSet,
        homePoints: newHome,
        awayPoints: newAway,
        snapshot,
      };

      const newState: LiveMatchState = {
        ...state,
        currentSetScore: { homePoints: newHome, awayPoints: newAway },
        homeLineup: newHomeLineup,
        awayLineup: newAwayLineup,
        servingTeamNow: newServingTeamNow,
        eventLog: [...state.eventLog, event],
      };

      // Controllo fine set
      if (isSetOver(newHome, newAway, state.currentSet, state.strategy)) {
        const setWinner: TeamSide = newHome > newAway ? 'HOME' : 'AWAY';
        const newSetsWon = {
          home: state.setsWon.home + (setWinner === 'HOME' ? 1 : 0),
          away: state.setsWon.away + (setWinner === 'AWAY' ? 1 : 0),
        };
        const completedSet: SetResult = {
          setNumber: state.currentSet,
          homePoints: newHome,
          awayPoints: newAway,
          durationMs: state.setStartedAt ? Date.now() - state.setStartedAt : 0,
          homeLineup: state.homeLineup.map((fp) => ({ ...fp })),
          awayLineup: state.awayLineup.map((fp) => ({ ...fp })),
        };
        const phase: GamePhase = isMatchOver(newSetsWon, state.strategy)
          ? 'MATCH_END'
          : 'SET_END_SUMMARY';

        return {
          ...newState,
          setsWon: newSetsWon,
          completedSets: [...state.completedSets, completedSet],
          currentPhase: phase,
        };
      }

      return newState;
    }

    case 'UNDO_LAST_EVENT': {
      if (state.eventLog.length === 0) return state;
      const lastEvent = state.eventLog[state.eventLog.length - 1];
      const restoredState = restoreSnapshot(state, lastEvent.snapshot);
      return {
        ...restoredState,
        eventLog: state.eventLog.slice(0, -1),
      };
    }

    case 'TIMEOUT': {
      const snapshot = takeSnapshot(state);
      const isHome = action.team === 'HOME';

      if (isHome && state.homeTimeouts >= TIMEOUTS_PER_SET) return state;
      if (!isHome && state.awayTimeouts >= TIMEOUTS_PER_SET) return state;

      const event: LiveGameEvent = {
        type: isHome ? 'TIMEOUT_HOME' : 'TIMEOUT_AWAY',
        timestamp: Date.now(),
        setNumber: state.currentSet,
        homePoints: state.currentSetScore.homePoints,
        awayPoints: state.currentSetScore.awayPoints,
        snapshot,
      };

      return {
        ...state,
        homeTimeouts: isHome ? state.homeTimeouts + 1 : state.homeTimeouts,
        awayTimeouts: !isHome ? state.awayTimeouts + 1 : state.awayTimeouts,
        eventLog: [...state.eventLog, event],
      };
    }

    case 'SUBSTITUTION': {
      const snapshot = takeSnapshot(state);
      const isHome = action.team === 'HOME';
      const lineup = isHome ? state.homeLineup : state.awayLineup;
      const bench = isHome ? state.homeBench : state.awayBench;

      const fieldPlayer = lineup.find((fp) => fp.playerId === action.outPlayerId);
      if (!fieldPlayer) return state;
      if (!bench.includes(action.inPlayerId)) return state;

      const newLineup = lineup.map((fp) =>
        fp.playerId === action.outPlayerId ? { ...fp, playerId: action.inPlayerId } : fp,
      );
      const newBench = bench.map((id) => (id === action.inPlayerId ? action.outPlayerId : id));

      const event: LiveGameEvent = {
        type: isHome ? 'SUBSTITUTION_HOME' : 'SUBSTITUTION_AWAY',
        timestamp: Date.now(),
        setNumber: state.currentSet,
        homePoints: state.currentSetScore.homePoints,
        awayPoints: state.currentSetScore.awayPoints,
        playerId: action.outPlayerId,
        substituteId: action.inPlayerId,
        snapshot,
      };

      return {
        ...state,
        homeLineup: isHome ? newLineup : state.homeLineup,
        awayLineup: !isHome ? newLineup : state.awayLineup,
        homeBench: isHome ? newBench : state.homeBench,
        awayBench: !isHome ? newBench : state.awayBench,
        eventLog: [...state.eventLog, event],
      };
    }

    case 'UPDATE_SHIRT_NUMBER': {
      const updatePlayer = (p: import('./liveMatchTypes.ts').LivePlayer) =>
        p.id === action.playerId ? { ...p, shirtNumber: action.shirtNumber } : p;
      if (action.side === 'HOME') {
        return {
          ...state,
          homeAllPlayers: state.homeAllPlayers.map(updatePlayer),
          homeCallup: state.homeCallup.map(updatePlayer),
        };
      }
      return {
        ...state,
        awayAllPlayers: state.awayAllPlayers.map(updatePlayer),
        awayCallup: state.awayCallup.map(updatePlayer),
      };
    }

    case 'START_NEW_SET': {
      if (!state.strategy) return state;
      const nextSet = state.currentSet + 1;
      const decidingSet = state.strategy === 'BEST_OF_3' ? 3 : 5;
      const isDeciding = nextSet === decidingSet;

      const baseState = {
        ...state,
        lineupSetupSide: 'HOME' as TeamSide,
        currentSet: nextSet,
        currentSetScore: { homePoints: 0, awayPoints: 0 },
        homeLineup: [],
        awayLineup: [],
        homeBench: state.homeCallup.map((p) => p.id),
        awayBench: state.awayCallup.map((p) => p.id),
        homeTimeouts: 0,
        awayTimeouts: 0,
        setStartedAt: null,
      };

      if (isDeciding) {
        // Set decisivo: sorteggio manuale (torna a SERVICE_SELECTION senza back button)
        return { ...baseState, currentPhase: 'SERVICE_SELECTION', servingTeam: null, servingTeamNow: null };
      }

      // Set normale: il servizio si alterna rispetto al set 1
      // set dispari → stessa squadra del set 1; set pari → squadra opposta
      const firstServer = state.servingTeam ?? 'HOME';
      const nextServer: TeamSide = nextSet % 2 === 1 ? firstServer : (firstServer === 'HOME' ? 'AWAY' : 'HOME');
      return { ...baseState, currentPhase: 'LINEUP_SETUP', servingTeamNow: nextServer };
    }

    case 'COMPLETE_MATCH':
      return { ...state, currentPhase: 'MATCH_END' };

    default:
      return state;
  }
}
